import { useState, useEffect } from 'react';
import { Button, Table, Tag, Space, Modal, message, Tooltip } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { usersApi, User } from '../../api/usersApi';
import UserFormModal from './UserFormModal';
import ResetPasswordModal from './ResetPasswordModal';

const { confirm } = Modal;

const roleName = (user: User) => user.role?.displayName || 'Fără rol';

const UsersListPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersApi.getAll();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error('Eroare la încărcarea utilizatorilor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsFormModalOpen(true);
  };

  const handleDelete = (user: User) => {
    confirm({
      title: 'Confirmare ștergere',
      icon: <ExclamationCircleOutlined />,
      content: `Sigur doriți să ștergeți utilizatorul ${user.firstName} ${user.lastName}?`,
      okText: 'Da, șterge',
      okType: 'danger',
      cancelText: 'Anulează',
      async onOk() {
        try {
          await usersApi.delete(user.id);
          message.success('Utilizator șters cu succes');
          loadUsers();
        } catch (error) {
          message.error('Eroare la ștergerea utilizatorului');
        }
      },
    });
  };

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    setIsResetPasswordModalOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormModalOpen(false);
    loadUsers();
  };

  const handleResetPasswordSuccess = () => {
    setIsResetPasswordModalOpen(false);
  };

  const columns = [
    {
      title: 'Nume',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: (a: User, b: User) => a.lastName.localeCompare(b.lastName),
    },
    {
      title: 'Prenume',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: (a: User, b: User) => a.firstName.localeCompare(b.firstName),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Rol',
      key: 'role',
      render: (_: unknown, record: User) => <Tag color="blue">{roleName(record)}</Tag>,
      filters: Array.from(new Set(users.map(roleName))).map((role) => ({
        text: role,
        value: role,
      })),
      onFilter: (value: boolean | React.Key, record: User) => roleName(record) === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>
          {status === 'ACTIVE' ? 'Activ' : 'Inactiv'}
        </Tag>
      ),
      filters: [
        { text: 'Activ', value: 'ACTIVE' },
        { text: 'Inactiv', value: 'INACTIVE' },
      ],
      onFilter: (value: boolean | React.Key, record: User) => record.status === value,
    },
    {
      title: 'Creat la',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('ro-RO'),
      sorter: (a: User, b: User) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      render: (_: unknown, record: User) => (
        <Space size="small">
          <Tooltip title="Editează">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Resetează parola">
            <Button type="link" icon={<KeyOutlined />} onClick={() => handleResetPassword(record)} />
          </Tooltip>
          <Tooltip title="Șterge">
            <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>Management Utilizatori</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Utilizator Nou
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} utilizatori`,
        }}
      />

      <UserFormModal
        open={isFormModalOpen}
        user={selectedUser}
        onSuccess={handleFormSuccess}
        onCancel={() => setIsFormModalOpen(false)}
      />

      <ResetPasswordModal
        open={isResetPasswordModalOpen}
        user={selectedUser}
        onSuccess={handleResetPasswordSuccess}
        onCancel={() => setIsResetPasswordModalOpen(false)}
      />
    </div>
  );
};

export default UsersListPage;
